import {
  Container,
  Title,
  Text,
  Stack,
  Modal,
  Table,
  Badge,
  Alert,
  Loader,
  Center,
} from "@mantine/core";
import { useState } from "react";
import { useDisclosure } from "@mantine/hooks";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  useCaseStatusesAdmin,
  useWorkflowsForCaseStatus,
} from "../../../hooks/admin/useWorkflowsAdmin";
import AdminDataTable, {
  AdminColumn,
} from "../../../components/admin/shared/AdminDataTable";
import { CaseStatusWithUsage } from "../../../services/workflows.service";
import { formatWorkflowType } from "../../../utils/formatters";

export default function CaseStatusesAdminPage() {
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedStatus, setSelectedStatus] =
    useState<CaseStatusWithUsage | null>(null);

  const { data: caseStatuses = [], isLoading } = useCaseStatusesAdmin();

  // Fetch workflows for selected status when modal is open
  const { data: statusWorkflows = [], isLoading: workflowsLoading } =
    useWorkflowsForCaseStatus(selectedStatus?.id);

  const handleRowClick = (status: CaseStatusWithUsage) => {
    setSelectedStatus(status);
    open();
  };

  const columns: AdminColumn<CaseStatusWithUsage>[] = [
    {
      key: "name",
      label: "Name",
      sortable: true,
    },
    {
      key: "description",
      label: "Description",
      sortable: true,
      render: (status) => (
        <Text size="sm" lineClamp={2}>
          {status.description || "—"}
        </Text>
      ),
    },
    {
      key: "workflowCount",
      label: "Used",
      sortable: true,
      render: (status) => (
        <Badge
          variant="light"
          color={status.workflowCount > 0 ? "blue" : "gray"}
        >
          {status.workflowCount}
        </Badge>
      ),
    },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={2}>Case Statuses</Title>
          <Text size="sm" c="dimmed" mt="xs">
            System-defined case statuses used across all workflows. These values
            are read-only.
          </Text>
        </div>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          Case statuses represent the discrete steps a case moves through during
          laboratory processing. The same status (e.g., "Pending Cutting") may
          be used by multiple workflows.
        </Alert>

        <AdminDataTable
          data={caseStatuses}
          columns={columns}
          isLoading={isLoading}
          searchPlaceholder="Search statuses..."
          emptyMessage="No case statuses found"
          getId={(status) => status.id}
          onRowClick={handleRowClick}
          pageSize={50}
        />

        <Text size="sm" c="dimmed">
          Showing {caseStatuses.length} statuses
        </Text>
      </Stack>

      {/* Detail Modal */}
      <Modal
        opened={opened}
        onClose={close}
        title={
          <Text fw={600} size="lg">
            Status: {selectedStatus?.name}
          </Text>
        }
        size="lg"
      >
        {selectedStatus && (
          <Stack gap="md">
            <div>
              <Text size="sm" c="dimmed">
                Description
              </Text>
              <Text>{selectedStatus.description || "No description"}</Text>
            </div>

            <div>
              <Text size="sm" c="dimmed" mb="xs">
                Used in Workflows
              </Text>
              {workflowsLoading ? (
                <Center py="md">
                  <Loader size="sm" />
                </Center>
              ) : statusWorkflows.length === 0 ? (
                <Text size="sm" c="dimmed" fs="italic">
                  This status is not currently used by any workflow.
                </Text>
              ) : (
                <Table withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Workflow</Table.Th>
                      <Table.Th>Order Position</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {statusWorkflows.map((mapping) => (
                      <Table.Tr key={mapping.workflowId}>
                        <Table.Td>
                          <Badge variant="light" color="blue">
                            {formatWorkflowType(mapping.workflowType)}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{mapping.orderPosition}</Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </div>

            {/* Special note for pass_through conditional routing */}
            {statusWorkflows.some(
              (m) =>
                m.workflowType === "pass_through" &&
                (selectedStatus.name === "Pending Cutting" ||
                  selectedStatus.name === "Pending Distribution")
            ) && (
              <Alert
                icon={<IconInfoCircle size={16} />}
                color="yellow"
                variant="light"
              >
                <Text size="sm">
                  <strong>Pass-Through uses conditional routing:</strong>
                  <br />
                  • If any slide is incomplete → starts at "Pending Cutting"
                  <br />• If all slides are complete → starts at "Pending
                  Distribution"
                </Text>
              </Alert>
            )}
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
